// Types mirror the handoff data model and the PocketBase collections.
// PocketBase adds `id`, `created`, `updated` to every record.

export type ThemeName =
  | "green" | "teal" | "blue" | "indigo" | "red" | "orange"
  | "graphite";

// Order shown in Settings. Swatch colors live in index.css as .swatch-<key>,
// since each must render its own accent while another theme is active.
export const THEME_META: { key: ThemeName; label: string; hint: string }[] = [
  { key: "green", label: "Green", hint: "Jade" },
  { key: "teal", label: "Teal", hint: "Blue-green" },
  { key: "blue", label: "Blue", hint: "Cool" },
  { key: "indigo", label: "Indigo", hint: "Deep blue" },
  { key: "red", label: "Red", hint: "Warm" },
  { key: "orange", label: "Orange", hint: "Amber" },
  { key: "graphite", label: "Graphite", hint: "Neutral" },
];
export type Mode = "light" | "dark" | "auto";
export type Density = "comfortable" | "compact";

export type ProjectStatus = "active" | "planning" | "hold" | "complete" | "archived";
/** Todoist has no workspaces below the Business plan, so this split is ours. */
export type ProjectCategory = "work" | "personal";
export type ItemType = "task" | "deliverable" | "milestone";
export type ItemStatus = "todo" | "doing" | "waiting" | "blocked" | "done";
export type Priority = "low" | "medium" | "high" | "urgent";
export type Recur = "none" | "daily" | "weekly" | "biweekly" | "monthly";

export interface Base {
  id: string;
  created: string;
  updated: string;
}

export interface UserRecord extends Base {
  email: string;
  name: string;
  theme: ThemeName;
  mode: Mode;
  digestDay: number; // 0-6
  digestEnabled: boolean;
  // google tokens are stored server-side (encrypted); never sent to the client.
}

/** Which burn-down a report should draw. Fixed-fee spends once over the life of
 *  the engagement; a retainer resets every month. Without this a report has to
 *  guess from whichever fields happen to be filled in. */
export type BillingType = "none" | "fixed" | "retainer" | "hourly" | "internal";

export interface Project extends Base {
  user: string;
  parent?: string; // another Project id — enables sub-projects / groups
  folderId?: string; // a Folder id — an alternative to `parent` for grouping (not both)
  kind?: "standard" | "inbox"; // "inbox" = per-user catch-all that replaces loose tasks
  name: string;
  client: string;
  description: string;
  color: string;
  status: ProjectStatus;
  start?: string;
  due?: string;
  googleTaskListId?: string;
  taskListName: string;
  scope: string;
  fee?: string;
  contract?: string;

  // -- Todoist mirror --------------------------------------------------------
  /** The natural key. Everything else about a mirrored record can change in
   *  Todoist except this, so joins and re-syncs hang off it. Absent on projects
   *  created by hand here, which the sync therefore never touches. */
  todoistId?: string;
  todoistParentId?: string;

  // -- Enrichment Todoist has nowhere to store -------------------------------
  /** Todoist has no workspaces below the Business plan, so this split is ours.
   *  Named `category` because `kind` is already standard|inbox. */
  category?: ProjectCategory;
  /** The authoritative client link. `client` (free text) stays the display
   *  name because the rest of the app reads it; rates resolve through this,
   *  because matching a rate on a typed name means a typo silently costs you
   *  the money on every entry logged against the project. */
  clientRef?: string;
  defaultRate?: number;
  currency?: string;
  billableDefault?: boolean;

  // -- Budget ----------------------------------------------------------------
  billingType?: BillingType;
  /** Fixed-fee: hours sold, spent once across the whole engagement. */
  budgetHours?: number;
  /** The numeric fee — the whole fee for fixed-fee, the monthly fee for a
   *  retainer. `fee` (text) stays for display — it reads "$62,000" and is shown
   *  as-is — but text cannot be divided by hours. */
  feeAmount?: number;
  /** Retainer: the monthly allowance, compared against that month alone. */
  retainerHours?: number;
}

export interface Phase extends Base {
  project: string;
  name: string;
  color: string;
  description?: string; // optional context shown under the phase name
  start?: string;
  end?: string;
  position: number;
  /** Mirrored Todoist section id, or "nosection:<projectId>" for the implicit
   *  catch-all phase that holds tasks filed directly under a project. */
  todoistId?: string;
}

export interface Item extends Base {
  phase: string;
  title: string;
  type: ItemType;
  status: ItemStatus;
  priority: Priority;
  start?: string;
  due?: string;
  notes: string;
  position: number;
  syncEnabled: boolean;
  googleTaskId?: string;
  googleTaskEtag?: string;
  clientVisible: boolean;
  recur: Recur;
  dependsOn?: string; // another Item id
  milestone?: string; // another Item id (of type "milestone") this work rolls up to
  parent?: string; // another Item id — the deliverable or milestone this task belongs to
  waitOn?: string; // free text: who you are waiting on. Empty means nobody.
  waitSince?: string; // stamped when waitOn is first set; cleared when waitOn is cleared

  // -- Todoist mirror --------------------------------------------------------
  todoistId?: string;
  todoistParentId?: string;
  /** Captured when the mirror sees the completion. The Todoist API only serves
   *  ~3 months of completions, so this is the durable record for anything
   *  older — it cannot be reconstructed from the API later. */
  completedAt?: string;
  /** Todoist labels, kept verbatim. They also drive `type`, `status` and
   *  `clientVisible` — see lib/todoistSync.ts. */
  labels?: string[];
  /** A recurring task's DEADLINE does not roll forward with its date, so the
   *  timeline has to know before trusting `due` on a repeating item. */
  isRecurring?: boolean;
  /**
   * Expected hours. Project Hub enrichment, never synced back — Todoist's
   * `duration` caps at 24 h and needs a time on the date, which rules it out
   * for planning. This is what turns Roadmap's load row from a count of items
   * due into a real forward load, and makes logged-vs-expected answerable.
   */
  estimateHours?: number;
}

// ---- Time (the layer Todoist has nowhere to store) -------------------------

export interface Client extends Base {
  user: string;
  name: string;
  defaultRate?: number;
  currency?: string;
  notes?: string;
  /** Printed on every expense package for this client ("Submission notes").
   *  Edited from the Expenses section, where it is used. */
  reimbursementNotes?: string;
}

// ---- Expenses (shared with the Reimburse phone app) ------------------------
// Both apps read and write these collections. Field names are Reimburse's —
// snake_case — because it wrote them first; see pocketbase/pb_schema_expenses.json.

export interface ExpenseCategory extends Base {
  user: string;
  name: string;
  /** A glyph key from components/ExpenseIcons. */
  icon?: string;
  tax_line?: string;
  /** Amount comes from miles × the IRS rate rather than being entered. */
  is_mileage?: boolean;
  sort?: number;
  /** Hidden from new expenses; existing ones keep it. */
  archived?: boolean;
}

export interface MileageRate extends Base {
  user: string;
  effective_date: string;
  rate: number;
  note?: string;
}

/** "archived" is what "Mark invoiced" sets — Reimburse's Invoiced tab. */
export type ExpenseReportStatus = "active" | "invoiced" | "archived";

export interface ExpenseReport extends Base {
  user: string;
  name: string;
  client: string;
  project?: string;
  type?: "trip" | "month";
  status?: ExpenseReportStatus;
  start_date?: string;
  end_date?: string;
  meal_mode?: "actuals" | "per_diem";
  per_diem_rate?: number;
  invoice_number?: string;
  invoiced_at?: string;
  notes?: string;
}

export interface Expense extends Base {
  user: string;
  report: string;
  /** Split child. Not created by either app yet; excluded from totals. */
  parent?: string;
  category?: string;
  date?: string;
  description?: string;
  amount?: number;
  miles?: number;
  mileage_rate?: number;
  is_mileage?: boolean;
  /** PocketBase file names. */
  receipts?: string[];
  receipt_captured?: boolean;
  added_to_quicken?: boolean;
  added_to_invoice?: boolean;
  tax_line?: string;
  notes?: string;
  reimbursable?: boolean;
  from_location?: string;
  to_location?: string;
  round_trip?: boolean;
  /** Demo only: receipt bytes as data URLs, keyed by file name. */
  receiptData?: Record<string, string>;
}

export type TimeSource = "timer" | "manual" | "migrated";

export interface TimeEntry extends Base {
  user: string;
  /** Null for time logged against a project rather than a specific task. */
  item?: string;
  /** Set whenever the work belongs to a project — derived from the task when
   *  there is one, so a project rollup is a single filter rather than a join.
   *  Absent for ad-hoc work that belongs to no project. */
  project?: string;
  client?: string;
  /** What ad-hoc time was: "Email", "Calls", "Admin". The only thing that keeps
   *  unattached time meaningful in a report once there is no project to group
   *  it under. */
  category?: string;
  /** Its task or project was deleted in Todoist. The hours are kept; the link
   *  is what broke. `titleSnapshot` is what the work was called. */
  orphaned?: boolean;
  titleSnapshot?: string;
  start: string;
  end?: string; // absent while a timer is running
  durationSeconds: number;
  note?: string;
  billable?: boolean;
  /** Internal / admin time: your own overhead, never client work or charged,
   *  even when it keeps a project (a proposal for a prospect). See lib/billing. */
  internal?: boolean;
  /** Copied from the project/client default at creation, so a later rate change
   *  never rewrites history. */
  rateSnapshot?: number;
  currency?: string;
  source?: TimeSource;
  /** When this entry went out on an invoice. Absent means not yet billed — the
   *  only thing that makes "unbilled" a real number rather than "billable". */
  invoicedAt?: string;
  /** The number from the accounting program that issued the invoice, so an
   *  entry can be traced to the bill it was on. Optional even when invoiced. */
  invoiceNumber?: string;
  /** Travel time: the percent of the normal rate applied (lib/travel.ts).
   *  `rateSnapshot` already holds the discounted rate; this records the share
   *  so it can be reversed exactly. Absent means billed at the full rate. */
  travelPct?: number;
  /** Set only by the eventual import from the standalone time-tracker. */
  legacyTimeTrackId?: string;
  legacySourceApp?: string;
}

/**
 * One line on a grouped invoice: several entries billed under one description.
 * `hours` is a hand-set figure for the line; absent means the logged hours,
 * rounded by the draft's increment. See lib/invoiceDraft.
 */
export interface InvoiceLine {
  id: string;
  description: string;
  entryIds: string[];
  hours?: number | null;
}

/**
 * A grouped invoice being built — saved so it can be picked up again, and kept
 * after it is marked invoiced so the month can be reprinted as it was sent.
 * The entries themselves are never changed by grouping or rounding.
 */
export interface InvoiceDraft extends Base {
  user: string;
  title: string;
  client?: string;
  /** Optional: narrows the entries to one of the client's projects. */
  project?: string;
  /** "YYYY-MM". */
  month: string;
  /** Hours; 0 means no rounding. */
  roundTo?: number;
  roundMode?: "nearest" | "up";
  showRates?: boolean;
  invoiceNumber?: string;
  status?: "draft" | "invoiced";
  invoicedAt?: string;
  lines: InvoiceLine[];
}

export interface ActiveTimer extends Base {
  user: string;
  /** ISO 8601. Elapsed is computed locally from this, so any device can pick up
   *  a timer started on another. At most one row ever exists. */
  startedAt: string;
  item?: string;
  project?: string;
  note?: string;
  billable?: boolean;
  internal?: boolean;
}

export interface SyncState extends Base {
  user: string;
  /** Todoist's incremental cursor. Absent means the next run is a full sync. */
  todoistSyncToken?: string;
  lastSyncedAt?: string;
  completionsBackfilledTo?: string;
  lastError?: string;
}

export interface Subtask extends Base {
  item: string;
  title: string;
  note?: string; // optional free-text note attached to a checklist item
  done: boolean;
  position: number;
  googleTaskId?: string;
}

export interface Contact extends Base {
  project: string;
  name: string;
  role: string;
  email: string;
  position: number;
}

export interface Artifact extends Base {
  project: string;
  label: string;
  kind: string; // "PDF" | "Sheet" | "Doc" | "Slides" | "Link"
  driveFileId?: string;
  driveUrl?: string;
  // Stored-file upload (Build 2). In LIVE mode `file` is the PocketBase filename
  // on the record; the download URL is built with pb.files.getUrl(). In DEMO mode
  // there is no file backend, so a small file is kept inline as a data URL
  // (`fileData`) with its original name (`fileName`). An artifact is either a
  // linked URL (driveUrl) OR an uploaded file — never required to have both.
  file?: string;
  fileName?: string;
  fileData?: string;
  position: number;
}

// A lightweight grouping for projects, parallel to (and mutually exclusive with)
// parent/child nesting. Deleting a folder re-files its projects to "Unfiled"
// rather than deleting them.
export interface Folder extends Base {
  user: string;
  name: string;
  color: string;
  position: number;
}

export interface Note extends Base {
  project: string;
  body: string;
  pinned: boolean;
}

// A dated entry in a project's scope log. `impact` is free text so it can read
// "+$4,000" or "no change" without forcing a number.
export interface ScopeChange extends Base {
  project: string;
  date: string;
  body: string;
  impact: string;
}

// ---- Vocabulary (labels, glyphs, token names) ------------------------------

export const STATUS_ORDER: ItemStatus[] = ["todo", "doing", "waiting", "blocked", "done"];

export const STATUS_META: Record<
  ItemStatus,
  { label: string; bg: string; text: string; dot: string }
> = {
  todo: { label: "Not started", bg: "var(--tint)", text: "var(--ink3)", dot: "var(--muted2)" },
  doing: { label: "In progress", bg: "var(--accentTint)", text: "var(--accent)", dot: "var(--accent)" },
  waiting: { label: "Waiting on someone", bg: "var(--purpleTint)", text: "var(--purple)", dot: "var(--purple)" },
  blocked: { label: "Blocked", bg: "var(--badTint)", text: "var(--bad)", dot: "var(--bad)" },
  done: { label: "Completed", bg: "var(--goodTint)", text: "var(--goodInk)", dot: "var(--good)" },
};

// A project is Active or Archived, matching Todoist. Planning, hold and
// complete are older stages still in the schema: they read as Active, and the
// sync rewrites them to "active" on a mirrored project.
export const PROJECT_STATUS_LABEL: Record<ProjectStatus, string> = {
  active: "Active",
  planning: "Active",
  hold: "Active",
  complete: "Active",
  archived: "Archived",
};

/** The two statuses a project is offered. */
export const PROJECT_STATUSES: ProjectStatus[] = ["active", "archived"];

export const TYPE_META: Record<ItemType, { label: string; glyph: string; color: string }> = {
  task: { label: "Task", glyph: "○", color: "var(--muted)" },
  deliverable: { label: "Deliverable", glyph: "▣", color: "var(--accent)" },
  milestone: { label: "Milestone", glyph: "◆", color: "var(--purple)" },
};

export const PRIORITY_META: Record<Priority, { label: string; color: string }> = {
  low: { label: "Low", color: "var(--muted)" },
  medium: { label: "Medium", color: "var(--ink3)" },
  high: { label: "High", color: "var(--warn)" },
  urgent: { label: "Urgent", color: "var(--bad)" },
};

export const RECUR_LABEL: Record<Recur, string> = {
  none: "Does not repeat",
  daily: "Every day",
  weekly: "Every week",
  biweekly: "Every two weeks",
  monthly: "Every month",
};

export const RECUR_BADGE: Record<Recur, string> = {
  none: "once",
  daily: "daily",
  weekly: "weekly",
  biweekly: "2 wks",
  monthly: "monthly",
};

// Nine fixed identity swatches — do NOT change with theme.
export const PROJECT_PALETTE = [
  "#1F5D50", "#2B4C7E", "#8A4B2A", "#4A4E69", "#5C6B4A",
  "#7A5C7B", "#39707A", "#8C6D1F", "#9A4A5C",
];

// Calendars on the Today grid have their own, brighter set. The project
// swatches are deep on purpose (they're dots and text); as the light wash a
// calendar block sits on they all read as the same grey. These are mid-tones,
// so the wash is a clear tint and the text, a deeper mix of the same hue, still
// reads. The first five are the defaults by position, so they're far apart.
export const CALENDAR_PALETTE = [
  "#3B82F6", "#22A35A", "#8B5CF6", "#E0701A", "#E0569B", "#14B8A6",
  "#0EA5E9", "#E5484D", "#E5B21A", "#84B829", "#9F8FEF", "#64748B",
];
